/**
 * Resolve link URLs from references (resolve-app / resolve-slug). References are expected
 * to have unchanged ID keys and camelCased values (see camelCaseMapValues).
 */

export type ReferencesMap = Record<string, unknown>;

/**
 * Returns the URL path (slug) for an internal reference ID.
 *
 * @param refId - Reference ID (e.g. from internalReference.ref)
 * @param references - Map with ID keys and camelCased values (each entry has e.g. route.slug)
 * @returns Path with leading slash, or undefined if not found
 */
export const getSlugFromInternalRef = (refId: string, references: ReferencesMap): string | undefined => {
    if (!refId || typeof refId !== 'string') return undefined;
    const resolved = references[refId];
    if (!resolved || typeof resolved !== 'object') return undefined;
    const { route } = resolved as { route?: { slug?: string } };
    const slug = route?.slug;
    if (slug == null || typeof slug !== 'string') return undefined;
    return slug.startsWith('/') ? slug : `/${slug}`;
};

export interface LinkContentLike {
    /** Determines whether to use internalReference or externalUrl */
    linkType?: 'internal' | 'external';
    /** For internal links: object with ref (reference ID to resolve via references) */
    internalReference?: { ref?: string } | unknown;
    /** For external links: the URL string */
    externalUrl?: string;
}

export type LinkContentInput = Partial<LinkContentLike> & Record<string, unknown>;

/**
 * Resolves the URL for link content (menu items, buttons, rich-text links).
 * Internal links are resolved via references; external links use externalUrl.
 *
 * @param linkContent - Object with linkType, internalReference and/or externalUrl
 * @param references - Map with ID keys and camelCased values (see ReferencesMap)
 * @returns Resolved URL string or undefined
 */
export const getLinkUrlFromLinkContent = (
    linkContent: LinkContentLike | LinkContentInput | null | undefined,
    references: ReferencesMap,
): string | undefined => {
    if (!linkContent || typeof linkContent !== 'object') return undefined;
    const { linkType, externalUrl, internalReference } = linkContent as LinkContentLike;
    if (linkType === 'external' && typeof externalUrl === 'string') {
        return externalUrl;
    }

    if (linkType === 'internal' && internalReference) {
        const refId = (internalReference as { ref?: string }).ref;
        if (refId) return getSlugFromInternalRef(refId, references);
    }
    return undefined;
};
